import getData from '../../data-services/api';

/** Max submission cards inside one entry/batch. */
export const MAX_SUBMISSIONS = 12;

/** Allowed submission status values. */
export const STATUS = {
    PENDING: 'Pending',
    APPROVED: 'Approved',
};

/**
 * Submission groups use Salesforce fields:
 * - Submission_Group_ID__c: unique id shared by all cards in one entry (max 12)
 * - Submission_Name__c: display name for the group
 * Salesforce Name is still written as a technical token ETTC-{groupId}-{n}.
 * Legacy rows without group id fall back to Name parse, then legacy-{id}.
 */
export const makeBatchKey = () =>
    `b${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const makeGroupId = makeBatchKey;

export const buildTechnicalName = (groupId, submissionNumber) =>
    `ETTC-${groupId}-${submissionNumber}`;

/** @deprecated use buildTechnicalName */
export const buildSubmissionName = buildTechnicalName;

export const parseBatchKeyFromName = (name) => {
    if (!name || typeof name !== 'string') return null;
    const trimmed = name.trim();
    const pipeParts = trimmed.split('|');
    if (pipeParts.length >= 3 && pipeParts[0] === 'ETTC' && pipeParts[1]) {
        return pipeParts[1];
    }
    const hyphenMatch = /^ETTC-([A-Za-z0-9]+)-(\d+)$/.exec(trimmed);
    if (hyphenMatch) {
        return hyphenMatch[1];
    }
    return null;
};

const rowGroupId = (row) => {
    if (!row) return '';
    const raw = row.submission_group_id__c;
    if (raw == null || raw === '') return '';
    return String(raw).trim();
};

const rowSubmissionName = (row) => {
    if (!row) return '';
    const raw = row.submission_name__c;
    if (raw == null || raw === '') return '';
    return String(raw).trim();
};

/** Stable entry key for grouping list rows and loading a batch. */
export const getEntryKey = (row) => {
    const groupId = rowGroupId(row);
    if (groupId) return groupId;
    const parsed = parseBatchKeyFromName(row && row.name);
    if (parsed) return parsed;
    if (row && row.id != null) {
        return `legacy-${row.id}`;
    }
    return 'legacy-unknown';
};

const attachAccountNames = async (list) => {
    const sfids = [
        ...new Set(
            list
                .reduce((ids, row) => ids.concat([row.fcp__c, row.evaluated_by__c]), [])
                .filter(Boolean)
        ),
    ];
    if (!sfids.length) return list;

    const inList = sfids.map((id) => `'${toSqlValue(id)}'`).join(',');
    const nameRows = rows(
        await getData(
            `SELECT sfid, name FROM salesforce.Account WHERE sfid IN (${inList})`
        )
    );
    const names = {};
    nameRows.forEach((row) => {
        if (row && row.sfid) names[row.sfid] = row.name;
    });
    return list.map((row) => ({
        ...row,
        fcp_name: names[row.fcp__c] || row.fcp_name || '',
        evaluated_by_name: names[row.evaluated_by__c] || row.evaluated_by_name || '',
    }));
};

const loadSubmissions = async (whereSql) => {
    const query = `
        SELECT *
        FROM salesforce.FCP_Submission__c
        WHERE ${whereSql}
        ORDER BY createddate DESC
    `;
    const response = await getData(query);
    if (!response || response.error || response.status === false || !response.result) {
        const err = new Error(
            (response && (response.message || response.error)) ||
                'Could not load ETTC submissions.'
        );
        err.response = response;
        throw err;
    }
    return attachAccountNames(rows(response));
};

export const toSqlValue = (value) => {
    if (value === null || typeof value === 'undefined') return '';
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'null' || normalized === 'undefined') return '';
        return value.replace(/'/g, "''");
    }
    return String(value);
};

export const toSqlNullable = (value) => {
    if (value === null || typeof value === 'undefined' || value === '') {
        return 'NULL';
    }
    if (typeof value === 'number') {
        return String(value);
    }
    return `'${toSqlValue(value)}'`;
};

const rows = (response) => (response && response.result ? response.result : []);

const first = (response) => {
    const list = rows(response);
    return list.length ? list[0] : null;
};

export const listByFcp = async (fcpSfid) =>
    loadSubmissions(`fcp__c = '${toSqlValue(fcpSfid)}'`);

export const listByBatch = async (fcpSfid, batchKey) => {
    const all = await listByFcp(fcpSfid);
    return all
        .filter((r) => getEntryKey(r) === batchKey)
        .sort(
            (a, b) =>
                (parseInt(a.submission_number__c, 10) || 0) -
                (parseInt(b.submission_number__c, 10) || 0)
        );
};

/** Group submission rows into entries (one entry = up to 12 cards). */
export const groupSubmissionEntries = (submissionRows) => {
    const map = {};
    (submissionRows || []).forEach((row) => {
        const key = getEntryKey(row);
        if (!map[key]) {
            map[key] = {
                batchKey: key,
                isLegacy: key.startsWith('legacy-') || !rowGroupId(row),
                submissionName: rowSubmissionName(row) || '',
                submissions: [],
                submission_date__c: row.submission_date__c,
                evaluated_by_name: row.evaluated_by_name,
                evaluated_by__c: row.evaluated_by__c,
                fcp_name: row.fcp_name,
                fcp__c: row.fcp__c,
                createddate: row.createddate,
            };
        }
        map[key].submissions.push(row);
        if (!map[key].submissionName && rowSubmissionName(row)) {
            map[key].submissionName = rowSubmissionName(row);
        }
        if (row.createddate && (!map[key].createddate || row.createddate > map[key].createddate)) {
            map[key].createddate = row.createddate;
        }
        if (row.submission_date__c) {
            map[key].submission_date__c = row.submission_date__c;
        }
        if (row.evaluated_by_name) {
            map[key].evaluated_by_name = row.evaluated_by_name;
        }
        if (row.evaluated_by__c) {
            map[key].evaluated_by__c = row.evaluated_by__c;
        }
        if (row.fcp_name) {
            map[key].fcp_name = row.fcp_name;
        }
        if (row.fcp__c) {
            map[key].fcp__c = row.fcp__c;
        }
    });

    return Object.keys(map)
        .map((k) => {
            const entry = map[k];
            entry.submissions.sort(
                (a, b) =>
                    (parseInt(a.submission_number__c, 10) || 0) -
                    (parseInt(b.submission_number__c, 10) || 0)
            );
            const pending = entry.submissions.filter(
                (s) => s.submission_status__c !== STATUS.APPROVED
            ).length;
            const approved = entry.submissions.filter(
                (s) => s.submission_status__c === STATUS.APPROVED
            ).length;
            return {
                ...entry,
                submissionName: entry.submissionName || 'ETTC',
                total: entry.submissions.length,
                pending,
                approved,
            };
        })
        .sort((a, b) => String(b.createddate || '').localeCompare(String(a.createddate || '')));
};

/** Group FCP submissions into list entries (one entry = up to 12 cards). */
export const listEntriesByFcp = async (fcpSfid) => {
    const all = await listByFcp(fcpSfid);
    return groupSubmissionEntries(all);
};

export const listEntriesByEvaluator = async (evaluatorSfid) => {
    const all = await listByEvaluator(evaluatorSfid);
    return groupSubmissionEntries(all);
};

export const listByEvaluator = async (evaluatorSfid) =>
    loadSubmissions(`evaluated_by__c = '${toSqlValue(evaluatorSfid)}'`);

export const getById = async (id) => {
    const list = await loadSubmissions(`id = '${toSqlValue(String(id))}'`);
    return list.length ? list[0] : null;
};

export const countByFcp = async (fcpSfid) => {
    const query = `
        SELECT COUNT(*) AS count
        FROM salesforce.FCP_Submission__c
        WHERE fcp__c = '${toSqlValue(fcpSfid)}'
    `;
    const row = first(await getData(query));
    return row ? parseInt(row.count, 10) || 0 : 0;
};

export const nextSubmissionNumberForBatch = async (fcpSfid, batchKey) => {
    const batchRows = await listByBatch(fcpSfid, batchKey);
    const maxNum = batchRows.reduce((max, r) => {
        const n = parseInt(r.submission_number__c, 10) || 0;
        return n > max ? n : max;
    }, 0);
    return maxNum + 1;
};

export const searchFcpEvaluators = async (inputValue) => {
    const term = (inputValue || '').trim();
    const nameFilter = term
        ? `AND name ILIKE '%${toSqlValue(term)}%'`
        : '';
    const query = `
        SELECT id, sfid, name, billingcity
        FROM salesforce.Account
        WHERE recordtypeid = '${process.env.REACT_APP_FCP_RECORD_TYPE_ID}'
          AND sfid IS NOT NULL
          ${nameFilter}
        ORDER BY name ASC
        LIMIT 50
    `;
    const response = await getData(query);
    const list = response && response.result ? response.result : [];
    if (!response || !response.result) {
        console.error('FCP evaluator search failed', query, response);
    }
    return list.map((option) => ({
        label: option.billingcity
            ? `${option.name} (${option.billingcity})`
            : option.name,
        value: option.sfid,
        data: option,
    }));
};

export const createSubmission = async ({
    fcpSfid,
    evaluatedBySfid,
    submissionDate,
    imageUrl,
    pdfUrl,
    submissionNumber: forcedNumber,
    batchKey,
    submissionName,
}) => {
    if (!fcpSfid) {
        throw new Error('FCP is required.');
    }
    if (!evaluatedBySfid || evaluatedBySfid === fcpSfid) {
        throw new Error('Please select another FCP as evaluator.');
    }
    if (!imageUrl && !pdfUrl) {
        throw new Error('Each submission needs an image or PDF.');
    }
    if (!batchKey) {
        throw new Error('Submission group id is required.');
    }
    const trimmedName = (submissionName || '').trim();
    if (!trimmedName) {
        throw new Error('Submission name is required.');
    }

    const existingInBatch = await listByBatch(fcpSfid, batchKey);
    if (existingInBatch.length >= MAX_SUBMISSIONS) {
        throw new Error(`Maximum of ${MAX_SUBMISSIONS} submissions per entry allowed.`);
    }

    const submissionNumber =
        forcedNumber || (await nextSubmissionNumberForBatch(fcpSfid, batchKey));
    if (submissionNumber > MAX_SUBMISSIONS) {
        throw new Error(`Maximum of ${MAX_SUBMISSIONS} submissions per entry allowed.`);
    }

    const technicalName = toSqlValue(buildTechnicalName(batchKey, submissionNumber));
    const dateValue = submissionDate || new Date().toISOString().slice(0, 10);

    const query = `
        INSERT INTO salesforce.FCP_Submission__c (
            Name,
            Submission_Group_ID__c,
            Submission_Name__c,
            FCP__c,
            Evaluated_By__c,
            Submission_Number__c,
            Submission_Date__c,
            Submission_Status__c,
            Submission_Image__c,
            Submission_Document__c
        ) VALUES (
            '${technicalName}',
            '${toSqlValue(batchKey)}',
            '${toSqlValue(trimmedName)}',
            '${toSqlValue(fcpSfid)}',
            '${toSqlValue(evaluatedBySfid)}',
            ${submissionNumber},
            '${toSqlValue(dateValue)}',
            '${STATUS.PENDING}',
            ${toSqlNullable(imageUrl)},
            ${toSqlNullable(pdfUrl)}
        ) RETURNING id, sfid
    `;

    const result = await getData(query);
    if (!result || result.status === false || result.error) {
        throw new Error(
            (result && (result.message || result.error)) ||
                'Failed to create submission.'
        );
    }
    return first(result);
};

/** Save one entry/batch (max 12 cards inside that entry). */
export const saveOwnerBatch = async ({
    fcpSfid,
    fcpName,
    evaluatedBySfid,
    submissionDate,
    sections,
    batchKey,
    submissionName,
}) => {
    if (!fcpSfid) {
        throw new Error('FCP is required.');
    }
    if (!evaluatedBySfid || evaluatedBySfid === fcpSfid) {
        throw new Error('Please select another FCP as evaluator.');
    }
    if (!batchKey) {
        throw new Error('Submission group id is required.');
    }
    const trimmedName = (submissionName || '').trim();
    if (!trimmedName) {
        throw new Error('Submission name is required.');
    }

    const existing = await listByBatch(fcpSfid, batchKey);
    const newSections = (sections || []).filter((s) => !s.id);
    if (existing.length + newSections.length > MAX_SUBMISSIONS) {
        throw new Error(`Maximum of ${MAX_SUBMISSIONS} submissions per entry allowed.`);
    }

    const results = [];
    let nextNum = await nextSubmissionNumberForBatch(fcpSfid, batchKey);
    const touchedIds = {};

    for (const section of sections || []) {
        if (section.id) {
            const row = existing.find((e) => String(e.id) === String(section.id));
            if (!row) continue;
            if (row.submission_status__c === STATUS.APPROVED) continue;
            if (!section.dirty && rowSubmissionName(row) === trimmedName) {
                // Still sync shared evaluator/date/name when header changes.
                // Fall through if name differs; if nothing dirty and name matches, skip.
                const headerUnchanged =
                    row.evaluated_by__c === evaluatedBySfid &&
                    String(row.submission_date__c || '').slice(0, 10) ===
                        String(submissionDate || '').slice(0, 10);
                if (headerUnchanged) continue;
            }
            const updated = await updateOwnerFields({
                id: section.id,
                ownerSfid: fcpSfid,
                evaluatedBySfid,
                imageUrl: section.imageUrl,
                pdfUrl: section.pdfUrl,
                submissionDate,
                submissionName: trimmedName,
            });
            touchedIds[String(section.id)] = true;
            results.push(updated);
        } else {
            if (!section.imageUrl && !section.pdfUrl) {
                continue;
            }
            const created = await createSubmission({
                fcpSfid,
                fcpName,
                evaluatedBySfid,
                submissionDate,
                imageUrl: section.imageUrl,
                pdfUrl: section.pdfUrl,
                submissionNumber: nextNum,
                batchKey,
                submissionName: trimmedName,
            });
            nextNum += 1;
            results.push(created);
        }
    }

    for (const row of existing) {
        if (row.submission_status__c === STATUS.APPROVED) continue;
        if (touchedIds[String(row.id)]) continue;
        if (rowSubmissionName(row) === trimmedName) continue;
        results.push(
            await updateOwnerFields({
                id: row.id,
                ownerSfid: fcpSfid,
                evaluatedBySfid,
                imageUrl: row.submission_image__c,
                pdfUrl: row.submission_document__c,
                submissionDate,
                submissionName: trimmedName,
            })
        );
    }

    return results;
};

export const updateOwnerFields = async ({
    id,
    ownerSfid,
    evaluatedBySfid,
    imageUrl,
    pdfUrl,
    submissionDate,
    submissionName,
}) => {
    const existing = await getById(id);
    if (!existing) {
        throw new Error('Submission not found.');
    }
    if (existing.fcp__c !== ownerSfid) {
        throw new Error('You can only update your own submissions.');
    }
    if (existing.submission_status__c === STATUS.APPROVED) {
        throw new Error('Approved submissions cannot be edited.');
    }
    if (!evaluatedBySfid || evaluatedBySfid === ownerSfid) {
        throw new Error('Please select another FCP as evaluator.');
    }

    const nameClause =
        submissionName != null && String(submissionName).trim() !== ''
            ? `, Submission_Name__c = '${toSqlValue(String(submissionName).trim())}'`
            : '';

    const query = `
        UPDATE salesforce.FCP_Submission__c SET
            Evaluated_By__c = '${toSqlValue(evaluatedBySfid)}',
            Submission_Date__c = ${toSqlNullable(submissionDate)},
            Submission_Image__c = ${toSqlNullable(imageUrl)},
            Submission_Document__c = ${toSqlNullable(pdfUrl)}
            ${nameClause}
        WHERE id = '${toSqlValue(String(id))}'
          AND fcp__c = '${toSqlValue(ownerSfid)}'
          AND submission_status__c = '${STATUS.PENDING}'
        RETURNING id
    `;

    const result = await getData(query);
    if (!result || result.status === false || result.error) {
        throw new Error(
            (result && (result.message || result.error)) ||
                'Failed to update submission.'
        );
    }
    return first(result);
};

export const submitEvaluation = async ({
    id,
    evaluatorSfid,
    score,
    evaluationComments,
    overallClayModellingTechniqueOutcome,
    overallFeedbackAdditionalRemarks,
    rightUsageOfProductsMaterials,
    submissionPresentation,
    overallPaintingTechniqueOutcome,
}) => {
    const existing = await getById(id);
    if (!existing) {
        throw new Error('Submission not found.');
    }
    if (existing.evaluated_by__c !== evaluatorSfid) {
        throw new Error('Only the assigned evaluator can submit evaluation.');
    }
    if (existing.submission_status__c === STATUS.APPROVED) {
        throw new Error('Evaluation has already been submitted.');
    }
    if (score === null || score === undefined || score === '') {
        throw new Error('Score is required.');
    }
    const scoreNum = Number(score);
    if (!Number.isInteger(scoreNum) || scoreNum < 1 || scoreNum > 10) {
        throw new Error('Score must be between 1 and 10.');
    }

    const query = `
        UPDATE salesforce.FCP_Submission__c SET
            Score__c = ${scoreNum},
            Evaluation_Comments__c = ${toSqlNullable(evaluationComments)},
            Overall_Clay_Modelling_Technique_Outcome__c = ${toSqlNullable(
                overallClayModellingTechniqueOutcome
            )},
            Overall_Feedback_Additional_Remarks__c = ${toSqlNullable(
                overallFeedbackAdditionalRemarks
            )},
            Right_Usage_of_Products_Materials__c = ${toSqlNullable(
                rightUsageOfProductsMaterials
            )},
            Submission_Presentation__c = ${toSqlNullable(submissionPresentation)},
            Overall_Painting_Technique_Outcome__c = ${toSqlNullable(
                overallPaintingTechniqueOutcome
            )},
            Submission_Status__c = '${STATUS.APPROVED}'
        WHERE id = '${toSqlValue(String(id))}'
          AND evaluated_by__c = '${toSqlValue(evaluatorSfid)}'
          AND submission_status__c = '${STATUS.PENDING}'
        RETURNING id
    `;

    const result = await getData(query);
    if (!result || result.status === false || result.error) {
        throw new Error(
            (result && (result.message || result.error)) ||
                'Failed to submit evaluation.'
        );
    }
    const updated = first(result);
    if (!updated) {
        throw new Error('Evaluation could not be submitted. It may already be approved.');
    }
    return updated;
};

export const listAssignedFcpsForCmdi = async (cmdiSfid) => {
    const query = `
        SELECT a.id, a.sfid, a.name,
            COUNT(s.id) AS total_submissions,
            COUNT(CASE WHEN s.submission_status__c = '${STATUS.PENDING}' THEN 1 END) AS pending_count,
            COUNT(CASE WHEN s.submission_status__c = '${STATUS.APPROVED}' THEN 1 END) AS approved_count
        FROM salesforce.Account a
        INNER JOIN salesforce.FCP_Submission__c s ON s.fcp__c = a.sfid
        WHERE (a.Assigned_CMDI__c = '${toSqlValue(cmdiSfid)}'
            OR a.Secondary_CMDI__c = '${toSqlValue(cmdiSfid)}')
          AND a.recordtypeid = '${process.env.REACT_APP_FCP_RECORD_TYPE_ID}'
          AND a.sfid IS NOT NULL
        GROUP BY a.id, a.sfid, a.name
        HAVING COUNT(s.id) > 0
        ORDER BY a.name ASC
    `;
    return rows(await getData(query));
};

/** CMDIs reporting to AMMD that have at least one FCP with ETTC submissions. */
export const listCmdiForAmmd = async (ammdSfid) => {
    const query = `
        SELECT c.sfid, c.name,
            COUNT(s.id) AS total_submissions,
            COUNT(CASE WHEN s.submission_status__c = '${STATUS.PENDING}' THEN 1 END) AS pending_count,
            COUNT(CASE WHEN s.submission_status__c = '${STATUS.APPROVED}' THEN 1 END) AS approved_count
        FROM salesforce.Pidilite_User__c c
        INNER JOIN salesforce.Account a
            ON (a.Assigned_CMDI__c = c.sfid OR a.Secondary_CMDI__c = c.sfid)
        INNER JOIN salesforce.FCP_Submission__c s ON s.fcp__c = a.sfid
        WHERE c.Reporting_To_AMMD__c = '${toSqlValue(ammdSfid)}'
          AND c.sfid IS NOT NULL
          AND a.recordtypeid = '${process.env.REACT_APP_FCP_RECORD_TYPE_ID}'
          AND a.sfid IS NOT NULL
        GROUP BY c.sfid, c.name
        HAVING COUNT(s.id) > 0
        ORDER BY c.name ASC
    `;
    return rows(await getData(query));
};

export const listAssignedFcpsForAmmd = async (ammdSfid) => {
    const cmdiQuery = `
        SELECT sfid
        FROM salesforce.Pidilite_User__c
        WHERE Reporting_To_AMMD__c = '${toSqlValue(ammdSfid)}'
          AND sfid IS NOT NULL
    `;
    const cmdis = rows(await getData(cmdiQuery));
    if (!cmdis.length) {
        return [];
    }
    const cmdiIds = cmdis.map((c) => `'${toSqlValue(c.sfid)}'`).join(',');

    const query = `
        SELECT a.id, a.sfid, a.name,
            COUNT(s.id) AS total_submissions,
            COUNT(CASE WHEN s.submission_status__c = '${STATUS.PENDING}' THEN 1 END) AS pending_count,
            COUNT(CASE WHEN s.submission_status__c = '${STATUS.APPROVED}' THEN 1 END) AS approved_count
        FROM salesforce.Account a
        INNER JOIN salesforce.FCP_Submission__c s ON s.fcp__c = a.sfid
        WHERE a.Assigned_CMDI__c IN (${cmdiIds})
          AND a.recordtypeid = '${process.env.REACT_APP_FCP_RECORD_TYPE_ID}'
          AND a.sfid IS NOT NULL
        GROUP BY a.id, a.sfid, a.name
        HAVING COUNT(s.id) > 0
        ORDER BY a.name ASC
    `;
    return rows(await getData(query));
};

export const resolveMode = (user, submission, forcedMode) => {
    if (forcedMode === 'readonly') {
        return 'readonly';
    }
    if (!user) {
        return 'deny';
    }
    if (user.role === 'CMDI' || user.role === 'AMMD') {
        return 'readonly';
    }
    if (user.role !== 'FCP') {
        return 'deny';
    }
    if (!submission) {
        return 'owner';
    }
    if (user.sfid && submission.evaluated_by__c === user.sfid) {
        return 'evaluator';
    }
    if (user.sfid && submission.fcp__c === user.sfid) {
        return 'owner';
    }
    return 'deny';
};

export const probeTable = async () => {
    try {
        const result = await getData(
            `SELECT id FROM salesforce.FCP_Submission__c LIMIT 1`
        );
        return {
            ok: !(result && (result.error || result.status === false)),
            result,
        };
    } catch (error) {
        return { ok: false, error };
    }
};

export const cmdiHasAccessToFcp = async (cmdiSfid, fcpSfid) => {
    const query = `
        SELECT id FROM salesforce.Account
        WHERE sfid = '${toSqlValue(fcpSfid)}'
          AND (Assigned_CMDI__c = '${toSqlValue(cmdiSfid)}'
            OR Secondary_CMDI__c = '${toSqlValue(cmdiSfid)}')
          AND recordtypeid = '${process.env.REACT_APP_FCP_RECORD_TYPE_ID}'
        LIMIT 1
    `;
    return !!first(await getData(query));
};

export const ammdHasAccessToFcp = async (ammdSfid, fcpSfid) => {
    const query = `
        SELECT a.id
        FROM salesforce.Account a
        INNER JOIN salesforce.Pidilite_User__c c
            ON c.sfid = a.Assigned_CMDI__c
        WHERE a.sfid = '${toSqlValue(fcpSfid)}'
          AND c.Reporting_To_AMMD__c = '${toSqlValue(ammdSfid)}'
          AND a.recordtypeid = '${process.env.REACT_APP_FCP_RECORD_TYPE_ID}'
        LIMIT 1
    `;
    return !!first(await getData(query));
};
